import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { PERSONALIZED_PRODUCTS, CONTACT } from "@/lib/products";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppBubble from "@/components/site/WhatsAppBubble";
import Seo from "@/components/Seo";
import Breadcrumbs, { breadcrumbSchema } from "@/components/site/Breadcrumbs";
import { ArrowLeft, X, ChevronLeft, ChevronRight, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { trackWhatsAppClick, trackProductView } from "@/lib/analytics";

const orderOnWhatsApp = (product, designCode) => {
  trackWhatsAppClick({ placement: "samples_page", productId: product.id });
  const msg = [
    `Hi Shine on Coin, I'd like to order:`,
    `*${product.name}*`,
    designCode ? `Design reference: ${designCode}` : null,
    product.designLabel ? `Style: ${product.designLabel}` : null,
    "",
    "Please share pricing and next steps.",
  ]
    .filter(Boolean)
    .join("\n");
  window.open(`${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
};

const WEIGHT_LABELS = {
  "5g": "5 gram",
  "10g": "10 gram",
  "20g": "20 gram",
  "30g": "30 gram",
  "100g": "100 gram",
  "1g": "1 gram",
  "2g": "2 gram",
  "3g": "3 gram",
  "7g": "7 gram",
  multi: "Multi-Coin",
};

const TYPE_LABELS = {
  coin: "Silver Coin",
  momento: "Momento",
  locket: "Gold Locket",
  plate: "Silver Plate",
  bracelet: "Bracelet",
};

export default function SamplesPage() {
  const { type, design, weight } = useParams();
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState(-1);

  const matches = useMemo(
    () =>
      PERSONALIZED_PRODUCTS.filter(
        (p) =>
          p.productType === type &&
          p.designType === design &&
          (p.weights || []).includes(weight)
      ),
    [type, design, weight]
  );

  const samples = useMemo(() => {
    const list = [];
    matches.forEach((p) => {
      (p.gallery || [p.image]).forEach((src, idx) => {
        list.push({ src, product: p, key: `${p.id}-${idx}` });
      });
    });
    return list;
  }, [matches]);

  useEffect(() => {
    if (lightbox < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(-1);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % samples.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + samples.length) % samples.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, samples.length]);

  const designLabel = matches[0]?.designLabel;
  useEffect(() => {
    if (matches[0]) trackProductView(matches[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches[0]?.id]);
  const isBraceletRange = matches[0]?.designType === "stretch-belt-nameplate";
  const heading = designLabel
    ? isBraceletRange
      ? designLabel
      : `${designLabel} — ${WEIGHT_LABELS[weight] || weight}`
    : `${WEIGHT_LABELS[weight] || weight} ${TYPE_LABELS[type] || type} Designs`;
  const categoryLabel = matches[0]?.productTypeLabel || TYPE_LABELS[type] || type;
  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Catalog", href: "/catalog" },
    { label: categoryLabel, href: `/catalog?type=${type}` },
    { label: heading, href: `/samples/${type}/${design}/${weight}` },
  ];
  const description = `Every hand-engraved ${(matches[0]?.designLabel || matches[0]?.name || "photo coin").toLowerCase()} design we've made in the ${WEIGHT_LABELS[weight] || weight} range. Real customer engravings — order on WhatsApp.`;
  const productSchema = matches[0]
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: matches[0].name,
        description: matches[0].blurb,
        image: samples.slice(0, 6).map((s) => s.src),
        brand: { "@type": "Brand", name: "Shine on Coin" },
        category: matches[0].productTypeLabel,
        offers: {
          "@type": "Offer",
          priceCurrency: "INR",
          availability: "https://schema.org/InStock",
          url: `https://shineoncoin.com/samples/${type}/${design}/${weight}`,
        },
      }
    : null;

  return (
    <div className="relative min-h-screen">
      <Seo
        title={heading}
        description={description}
        path={`/samples/${type}/${design}/${weight}`}
        image={matches[0]?.image}
        type="product"
        jsonLd={productSchema ? [breadcrumbSchema(breadcrumbItems), productSchema] : [breadcrumbSchema(breadcrumbItems)]}
      />
      <Navbar />
      <main className="relative pt-32 md:pt-40 pb-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Breadcrumbs items={breadcrumbItems} testId="samples-breadcrumbs" />
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <Link
              to="/#personalize"
              data-testid="samples-back-link"
              className="inline-flex items-center gap-2 text-white/60 hover:text-[#d4af37] text-sm tracking-widest-2 uppercase transition"
            >
              <ArrowLeft size={16} /> Back to Personalize
            </Link>
            <Link
              to={`/catalog?type=${type}`}
              data-testid="samples-category-link"
              className="inline-flex items-center gap-2 text-white/60 hover:text-[#d4af37] text-sm tracking-widest-2 uppercase transition"
            >
              View all {categoryLabel}
            </Link>
          </div>

          <div className="mt-6 flex flex-col md:flex-row md:flex-wrap md:items-end md:justify-between gap-6">
            <div className="min-w-0">
              <div className="text-[10px] md:text-xs tracking-widest-2 uppercase text-[#d4af37]">All Similar Samples</div>
              <h1 className="mt-3 font-display text-3xl sm:text-4xl md:text-6xl text-white leading-[1.08] break-words">
                {heading}
              </h1>
              <p className="mt-4 text-white/60 text-sm md:text-base max-w-2xl">
                Every real engraving we've crafted matching your selection. Tap any tile to zoom.
                Prefer a specific style? Message us on WhatsApp with the design number and we'll
                reproduce it for you.
              </p>
            </div>
            <div className="text-white/50 text-[11px] uppercase tracking-widest-2">
              {samples.length} sample{samples.length === 1 ? "" : "s"} · {matches.length} product line{matches.length === 1 ? "" : "s"}
            </div>
          </div>

          {samples.length === 0 ? (
            <div className="mt-16 glass rounded-3xl p-16 text-center text-white/60">
              No samples yet for this combination. Please head back and pick another option.
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {samples.map((s, idx) => {
                const num = String(idx + 1).padStart(2, "0");
                const designCode = `${s.product.designType?.toUpperCase().replace(/-/g, "") || "SOC"}-${num}`;
                const copyCode = (e) => {
                  e.stopPropagation();
                  navigator.clipboard?.writeText(designCode);
                  toast.success(`Copied ${designCode}`);
                };
                return (
                <button
                  key={s.key}
                  data-testid={`sample-tile-${idx}`}
                  onClick={() => setLightbox(idx)}
                  className="group relative aspect-square glass rounded-2xl overflow-hidden border border-white/10 hover:border-[#d4af37]/50 transition"
                >
                  <img
                    src={s.src}
                    alt={s.product.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Always-visible design number badge */}
                  <div className="absolute top-2 left-2 flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-black/70 backdrop-blur border border-[#d4af37]/40">
                    <span className="text-[10px] tracking-widest-2 uppercase text-[#d4af37] font-medium">
                      Design {num}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`sample-copy-${idx}`}
                      onClick={copyCode}
                      onKeyDown={(e) => (e.key === "Enter" ? copyCode(e) : null)}
                      title={`Copy code ${designCode}`}
                      className="w-5 h-5 rounded-full bg-[#d4af37]/20 hover:bg-[#d4af37] hover:text-black flex items-center justify-center text-[#d4af37] transition"
                    >
                      <Copy size={10} />
                    </span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 transition">
                    <div className="text-xs text-white line-clamp-1">{s.product.name}</div>
                  </div>
                </button>
                );
              })}
            </div>
          )}

          <div className="mt-16 glass rounded-3xl p-6 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="min-w-0">
              <div className="font-display text-xl sm:text-2xl text-white">Like what you see?</div>
              <p className="text-white/60 text-sm mt-2 max-w-md">
                We take every order over WhatsApp. Share the design number
                and we'll reply with a quote, samples and a delivery estimate.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button
                data-testid="samples-whatsapp-cta"
                onClick={() => matches[0] && orderOnWhatsApp(matches[0])}
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 bg-[#25D366] text-black font-medium tracking-wide hover:brightness-110 transition w-full sm:w-auto"
              >
                <MessageCircle size={18} /> Order via WhatsApp
              </button>
              <button
                data-testid="samples-personalize-cta"
                onClick={() => navigate("/#personalize")}
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 glass glass-hover border border-white/15 text-white/90 tracking-wide transition w-full sm:w-auto"
              >
                Explore More
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppBubble />

      {lightbox >= 0 && samples[lightbox] && (
        <Lightbox
          samples={samples}
          index={lightbox}
          setIndex={setLightbox}
          close={() => setLightbox(-1)}
        />
      )}
    </div>
  );
}

function Lightbox({ samples, index, setIndex, close }) {
  const s = samples[index];
  const total = samples.length;
  const goPrev = () => setIndex((index - 1 + total) % total);
  const goNext = () => setIndex((index + 1) % total);

  // Touch-swipe on mobile
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 45) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  const num = String(index + 1).padStart(2, "0");
  const designCode = `${s.product.designType?.toUpperCase().replace(/-/g, "") || "SOC"}-${num}`;
  const copyCode = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(designCode);
    toast.success(`Copied ${designCode}`);
  };

  return (
    <div
      role="dialog"
      data-testid="samples-lightbox"
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex items-center justify-center px-4 py-8"
      onClick={close}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button
        data-testid="lightbox-close"
        onClick={(e) => {
          e.stopPropagation();
          close();
        }}
        className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 glass rounded-full flex items-center justify-center text-white hover:bg-[#d4af37] hover:text-black transition z-10"
      >
        <X size={20} />
      </button>

      <button
        data-testid="lightbox-prev"
        onClick={(e) => {
          e.stopPropagation();
          goPrev();
        }}
        className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 glass rounded-full flex items-center justify-center text-white hover:bg-[#d4af37] hover:text-black transition z-10"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        data-testid="lightbox-next"
        onClick={(e) => {
          e.stopPropagation();
          goNext();
        }}
        className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 glass rounded-full flex items-center justify-center text-white hover:bg-[#d4af37] hover:text-black transition z-10"
      >
        <ChevronRight size={22} />
      </button>

      <div
        className="relative max-w-5xl w-full max-h-full flex flex-col items-center gap-3 md:gap-4 px-2"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={s.src}
          alt={s.product.name}
          className="max-h-[58vh] md:max-h-[70vh] max-w-full object-contain rounded-2xl select-none"
          draggable={false}
        />
        <div className="text-center w-full px-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-[#d4af37]/40">
            <span className="text-[11px] tracking-widest-2 uppercase text-[#d4af37] font-medium">
              Design {num}
            </span>
            <button
              data-testid="lightbox-copy-code"
              onClick={copyCode}
              title={`Copy code ${designCode}`}
              className="w-6 h-6 rounded-full bg-[#d4af37]/20 hover:bg-[#d4af37] hover:text-black flex items-center justify-center text-[#d4af37] transition"
            >
              <Copy size={12} />
            </button>
          </div>
          <div className="mt-3 font-display text-xl sm:text-2xl text-white line-clamp-2 break-words">{s.product.name}</div>
          <div data-testid="lightbox-counter" className="mt-2 text-[11px] tracking-widest-2 uppercase text-white/50">
            Sample {index + 1} of {total} · Swipe or use ← →
          </div>
          <button
            data-testid="lightbox-order-whatsapp"
            onClick={(e) => {
              e.stopPropagation();
              orderOnWhatsApp(s.product, designCode);
            }}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 bg-[#25D366] text-black font-medium text-sm hover:brightness-110 transition"
          >
            <MessageCircle size={16} /> Order this Design via WhatsApp
          </button>

          {/* Dot indicators (cap to 20 to avoid overflow on very large sets) */}
          <div className="mt-4 flex items-center justify-center gap-1.5 flex-wrap max-w-md mx-auto">
            {samples.slice(0, Math.min(total, 20)).map((_, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex(i);
                }}
                aria-label={`Go to sample ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-6 bg-[#d4af37]" : "w-1.5 bg-white/30 hover:bg-white/60"
                }`}
              />
            ))}
            {total > 20 && (
              <span className="text-[10px] text-white/40 ml-1">+{total - 20}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
